import React, {FC} from 'react';

import {KeyboardActionsEnum} from '../../../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {CustomError} from '../../../../../../../libs/shared/core/src/helpers/types/customErrors';
import {ButtonVariantsEnum} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupSpacer} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {SameWidthButton} from '../../../../../../../libs/shared/ui/src/components/buttons/sameWidthButton';
import {Dialog} from '../../../../../../../libs/shared/ui/src/components/dialogs/dialog';
import {DialogButtonGroup} from '../../../../../../../libs/shared/ui/src/components/dialogs/dialogButtonGroup';
import {DialogContent} from '../../../../../../../libs/shared/ui/src/components/dialogs/dialogContent';
import {DialogHeader} from '../../../../../../../libs/shared/ui/src/components/dialogs/dialogHeader';
import {Icon} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {KeyboardEventHandlers} from '../../../../../../../libs/shared/ui/src/components/keyboard/keyboardContext';
import {KeyboardShortcuts} from '../../../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {VisualSizesEnum} from '../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {useUserTheme} from '../../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {SplitMaxLimitError} from '../../../../../../services/tasks/splitConversationTask';

/*
 * Props.
 */

interface SplitErrorDialogProps {
  onRequestClose: () => void;
  splitError: CustomError;
}

/*
 * Component.
 */

export const SplitErrorDialog: FC<SplitErrorDialogProps> = (props) => {
  const {onRequestClose, splitError} = props;
  const theme = useUserTheme();
  const keyHandlers: KeyboardEventHandlers = {[KeyboardActionsEnum.CONFIRM]: onRequestClose};
  const splitErrorProps = computeSplitErrorProps(splitError);

  if (!splitErrorProps) {
    return null;
  }

  return (
    <Dialog onRequestClose={onRequestClose}>
      <KeyboardShortcuts handlers={keyHandlers}>
        <DialogHeader
          title={splitErrorProps.errorTitle}
          titleIcon={<Icon name="warningFilledTriangle" color={theme.palette.red.shade40} size={16} />}
        />
        <DialogContent>{splitErrorProps.errorMessage}</DialogContent>
        <DialogButtonGroup>
          <ButtonGroupSpacer />
          <SameWidthButton
            variant={ButtonVariantsEnum.PRIMARY}
            size={VisualSizesEnum.MEDIUM}
            onClick={onRequestClose}
          >
            <Trans id="split-error-dialog-ok-button">OK</Trans>
          </SameWidthButton>
        </DialogButtonGroup>
      </KeyboardShortcuts>
    </Dialog>
  );
};

/*
 * Helpers.
 */

function computeSplitErrorProps(error: CustomError) {
  if (error instanceof SplitMaxLimitError) {
    return {
      errorTitle: <Trans id="splitconversation-error-title">Unable to split conversation</Trans>,
      errorMessage: (
        <Trans id="splitconversation-error">
          Up to 50 messages can be carried over into a newly split conversation. Split from a more recent
          message.
        </Trans>
      ),
    };
  }

  return undefined;
}
